/**
 * Select All helpers for Notification Specifics (section + column).
 * Matches approved select-all mockups (v4 / v4b).
 */

import {
  LAST24H_STATUS_ROWS,
  REMINDER_TIMING_ROWS,
  ROLE_COLUMNS,
  STATUS_CHANGES_ROWS,
  STATUS_CHANGES_WRITABLE,
  type NotificationRole,
  type NotificationSpecifics,
  type UserNotificationSettingsV2,
} from './notificationPrefs';

export type AggregateSelectState = 'all' | 'none' | 'partial';

export type SelectAllApplyResult = {
  next: UserNotificationSettingsV2;
  paths: string[];
};

export function aggregateSelectState(
  values: ReadonlyArray<boolean>
): AggregateSelectState {
  if (values.length === 0) return 'none';
  let on = 0;
  for (const value of values) {
    if (value) on += 1;
  }
  if (on === 0) return 'none';
  if (on === values.length) return 'all';
  return 'partial';
}

function cloneSettings(
  settings: UserNotificationSettingsV2
): UserNotificationSettingsV2 {
  return structuredClone(settings);
}

function setReminderCell(
  next: UserNotificationSettingsV2,
  timing: keyof NotificationSpecifics['jobReminders'],
  role: NotificationRole,
  checked: boolean,
  paths: string[]
) {
  if (next.specifics.jobReminders[timing][role] === checked) return;
  next.specifics.jobReminders[timing][role] = checked;
  paths.push(`specifics.jobReminders.${timing}.${role}`);
}

function setSummaryRole(
  next: UserNotificationSettingsV2,
  window: 'next24Hours' | 'last24Hours',
  role: NotificationRole,
  checked: boolean,
  paths: string[]
) {
  if (next.specifics.jobSummaries[window][role] === checked) return;
  next.specifics.jobSummaries[window][role] = checked;
  paths.push(`specifics.jobSummaries.${window}.${role}`);
}

function setStatusIncludeCell(
  next: UserNotificationSettingsV2,
  status: keyof NotificationSpecifics['jobSummaries']['last24Hours']['statusChangesToInclude'],
  role: NotificationRole,
  checked: boolean,
  paths: string[]
) {
  const current =
    next.specifics.jobSummaries.last24Hours.statusChangesToInclude[status][
      role
    ];
  if (current === checked) return;
  next.specifics.jobSummaries.last24Hours.statusChangesToInclude[status][role] =
    checked;
  paths.push(
    `specifics.jobSummaries.last24Hours.statusChangesToInclude.${status}.${role}`
  );
}

function setStatusChangesCell(
  next: UserNotificationSettingsV2,
  status: (typeof STATUS_CHANGES_ROWS)[number]['key'],
  role: NotificationRole,
  checked: boolean,
  paths: string[]
) {
  if (!STATUS_CHANGES_WRITABLE[status].includes(role)) return;
  if (Boolean(next.specifics.statusChanges[status]?.[role]) === checked) return;
  next.specifics.statusChanges[status] = {
    ...next.specifics.statusChanges[status],
    [role]: checked,
  };
  paths.push(`specifics.statusChanges.${status}.${role}`);
}

export function reminderSectionValues(
  jobReminders: NotificationSpecifics['jobReminders']
): boolean[] {
  const values: boolean[] = [];
  for (const row of REMINDER_TIMING_ROWS) {
    for (const col of ROLE_COLUMNS) {
      values.push(jobReminders[row.key][col.key]);
    }
  }
  return values;
}

export function reminderColumnValues(
  jobReminders: NotificationSpecifics['jobReminders'],
  role: NotificationRole
): boolean[] {
  return REMINDER_TIMING_ROWS.map((row) => jobReminders[row.key][role]);
}

export function applyReminderSectionSelectAll(
  settings: UserNotificationSettingsV2,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  for (const row of REMINDER_TIMING_ROWS) {
    for (const col of ROLE_COLUMNS) {
      setReminderCell(next, row.key, col.key, checked, paths);
    }
  }
  return { next, paths };
}

export function applyReminderColumnSelectAll(
  settings: UserNotificationSettingsV2,
  role: NotificationRole,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  for (const row of REMINDER_TIMING_ROWS) {
    setReminderCell(next, row.key, role, checked, paths);
  }
  return { next, paths };
}

/** Next/Last roles only (not send times; status-include UI removed). */
export function summarySectionValues(
  jobSummaries: NotificationSpecifics['jobSummaries']
): boolean[] {
  const values: boolean[] = [];
  for (const col of ROLE_COLUMNS) {
    values.push(jobSummaries.next24Hours[col.key]);
    values.push(jobSummaries.last24Hours[col.key]);
  }
  return values;
}

export function summaryParentColumnValues(
  jobSummaries: NotificationSpecifics['jobSummaries'],
  role: NotificationRole
): boolean[] {
  return [jobSummaries.next24Hours[role], jobSummaries.last24Hours[role]];
}

export function summaryStatusIncludeColumnValues(
  jobSummaries: NotificationSpecifics['jobSummaries'],
  role: NotificationRole
): boolean[] {
  return LAST24H_STATUS_ROWS.map(
    (row) => jobSummaries.last24Hours.statusChangesToInclude[row.key][role]
  );
}

export function applySummarySectionSelectAll(
  settings: UserNotificationSettingsV2,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  for (const col of ROLE_COLUMNS) {
    setSummaryRole(next, 'next24Hours', col.key, checked, paths);
    setSummaryRole(next, 'last24Hours', col.key, checked, paths);
  }
  return { next, paths };
}

export function applySummaryParentColumnSelectAll(
  settings: UserNotificationSettingsV2,
  role: NotificationRole,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  setSummaryRole(next, 'next24Hours', role, checked, paths);
  setSummaryRole(next, 'last24Hours', role, checked, paths);
  return { next, paths };
}

export function applySummaryStatusIncludeColumnSelectAll(
  settings: UserNotificationSettingsV2,
  role: NotificationRole,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  for (const row of LAST24H_STATUS_ROWS) {
    setStatusIncludeCell(next, row.key, role, checked, paths);
  }
  return { next, paths };
}

/** Keep-cells only (blanks excluded). */
export function statusChangesSectionValues(
  statusChanges: NotificationSpecifics['statusChanges']
): boolean[] {
  const values: boolean[] = [];
  for (const row of STATUS_CHANGES_ROWS) {
    for (const role of STATUS_CHANGES_WRITABLE[row.key]) {
      values.push(Boolean(statusChanges[row.key]?.[role]));
    }
  }
  return values;
}

export function statusChangesColumnValues(
  statusChanges: NotificationSpecifics['statusChanges'],
  role: NotificationRole
): boolean[] {
  const values: boolean[] = [];
  for (const row of STATUS_CHANGES_ROWS) {
    if (!STATUS_CHANGES_WRITABLE[row.key].includes(role)) continue;
    values.push(Boolean(statusChanges[row.key]?.[role]));
  }
  return values;
}

export function applyStatusChangesSectionSelectAll(
  settings: UserNotificationSettingsV2,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  for (const row of STATUS_CHANGES_ROWS) {
    for (const role of STATUS_CHANGES_WRITABLE[row.key]) {
      setStatusChangesCell(next, row.key, role, checked, paths);
    }
  }
  return { next, paths };
}

export function applyStatusChangesColumnSelectAll(
  settings: UserNotificationSettingsV2,
  role: NotificationRole,
  checked: boolean
): SelectAllApplyResult {
  const next = cloneSettings(settings);
  const paths: string[] = [];
  for (const row of STATUS_CHANGES_ROWS) {
    setStatusChangesCell(next, row.key, role, checked, paths);
  }
  return { next, paths };
}
